import { Fragment } from "react";
import { KopfLink } from "./kopf-link";
import { LeerZustand } from "../leerzustand";
import { breiteKlasse, cx, prozent } from "./breite";

/**
 * Stufe eines Segments = Farbe + Anzahl gefüllter Striche im Block
 * (3 Warn-Token `status-warn` = dunkelorange, 2 orange, 1 gelb, 0 neutral gestreift; Rot nie als Balkenfarbe, M-001).
 */
export type SegmentStufe = 3 | 2 | 1 | 0 | "voll";

export interface StatusSegment {
  label: string;
  wert: number;
  stufe: SegmentStufe;
}

const BALKEN: Record<SegmentStufe, string> = {
  // M-001: Rot nie als Balkenfarbe (`destructive` bleibt Lösch-/Gefahr-Aktionen); die höchste Stufe ist das Warn-Token (dunkles Orange).
  3: "overflow-hidden bg-status-warn rounded-full",
  2: "overflow-hidden bg-orange-400 rounded-full",
  1: "overflow-hidden bg-yellow-400 rounded-full",
  // "voll": Regelzustand (z. B. Aktiv) als ruhige neutrale Vollfläche, keine Schraffur als Hauptfüllung.
  voll: "overflow-hidden bg-primary-300 rounded-full",
  0: "overflow-hidden bg-surface bg-[linear-gradient(135deg,var(--color-surface-3)_25%,transparent_25%,transparent_50%,var(--color-surface-3)_50%,var(--color-surface-3)_75%,transparent_75%,transparent)] bg-size-[7px_7px] rounded-full",
};

const STRICH_LEER = "shrink-0 w-1 h-3.5 inline-block bg-surface-2 rounded-full";
const STRICHE: Record<SegmentStufe, [string, string, string]> = {
  3: [
    "shrink-0 w-1 h-3.5 inline-block bg-status-warn rounded-full",
    "shrink-0 w-1 h-3.5 inline-block bg-status-warn rounded-full",
    "shrink-0 w-1 h-3.5 inline-block bg-status-warn rounded-full",
  ],
  2: [
    "shrink-0 w-1 h-3.5 inline-block bg-orange-400 rounded-full",
    "shrink-0 w-1 h-3.5 inline-block bg-orange-400 rounded-full",
    STRICH_LEER,
  ],
  1: ["shrink-0 w-1 h-3.5 inline-block bg-yellow-400 rounded-full", STRICH_LEER, STRICH_LEER],
  0: [STRICH_LEER, STRICH_LEER, STRICH_LEER],
  voll: [STRICH_LEER, STRICH_LEER, STRICH_LEER],
};

const ZAHL = new Intl.NumberFormat("de-DE");

/**
 * Verteilung auf bis zu vier Stufen als segmentierter Balken mit Legenden-Raster.
 * Segmentbreite = echter Anteil am Gesamt (das Original überläuft absichtlich 100 %;
 * hier Anteile aus Daten). Segmente mit 0 entfallen im Balken, bleiben im Raster.
 * Ohne Bestand: Leerzustand statt Balken.
 */
export function SegmentStatusKarte({
  id,
  titel,
  segmente,
  link,
  leer,
}: {
  id: string;
  titel: string;
  segmente: StatusSegment[];
  link?: { href: string; text: string };
  /** Leerzustand, wenn die Summe 0 ist. */
  leer: { titel: string; text: string };
}) {
  const summe = segmente.reduce((s, x) => s + x.wert, 0);
  const titelId = `${id}-titel`;
  return (
    <>
      {/* Preline: data-display/data-display-sub-analytics-kpis/data-display-cat-progress-cards/segmented-status-progress-card */}
      <section aria-labelledby={titelId} className="p-5 flex flex-col bg-card border border-card-line shadow-xs rounded-xl overflow-hidden">
        {/* Header */}
        <div className="pb-5 flex flex-wrap justify-between items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id={titelId} className="font-medium text-foreground">
              {titel}
            </h2>
          </div>
          {link && (
            <div className="flex flex-wrap items-center gap-2">
              <KopfLink href={link.href} text={link.text} />
            </div>
          )}
        </div>
        {/* End Header */}

        {summe === 0 ? (
          <LeerZustand titel={leer.titel} text={leer.text} />
        ) : (
          <>
            {/* Multiple Progress Bars */}
            <div className="flex gap-1.5 w-full h-2.5">
              {segmente
                .filter((s) => s.wert > 0)
                .map((s) => {
                  const p = prozent(s.wert, summe);
                  return (
                    <div
                      key={s.label}
                      className={cx(BALKEN[s.stufe], breiteKlasse(p), "min-w-2")}
                      role="progressbar"
                      aria-label={s.label}
                      aria-valuenow={p}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  );
                })}
            </div>
            {/* End Multiple Progress Bars */}

            {/* Grid */}
            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {segmente.map((s) => (
                <Fragment key={s.label}>
                  <div>
                    <span className="block text-2xl text-foreground">{ZAHL.format(s.wert)}</span>
                    {/* Legend Indicator */}
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-0.5" aria-hidden="true">
                        {STRICHE[s.stufe].map((k, i) => (
                          <span key={i} className={k} />
                        ))}
                      </div>
                      <div className="grow">
                        <span className="block text-sm text-muted-foreground-1">{s.label}</span>
                      </div>
                    </div>
                    {/* End Legend Indicator */}
                  </div>
                </Fragment>
              ))}
            </div>
            {/* End Grid */}
          </>
        )}
      </section>
    </>
  );
}
